/**
 * Integration Hooks
 *
 * React Query hooks for integration management.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { integrationService } from '../services';
import type {
  GitHubConnection,
  GitHubRepository,
  GitLabConnection,
  GitLabProject,
  JiraConnection,
  JiraIssueLink,
  AzureDevOpsConnection,
  AzureDevOpsProject,
  ServiceNowConnection,
  SnykConnection,
  SnykProject,
  NotificationChannel,
  NotificationPreference,
  CreateGitHubConnectionRequest,
  CreateGitLabConnectionRequest,
  CreateJiraConnectionRequest,
  CreateAzureDevOpsConnectionRequest,
  CreateServiceNowConnectionRequest,
  CreateSnykConnectionRequest,
  CreateNotificationChannelRequest,
  ConnectionTestResult,
  SyncResult,
} from '../types';

// Query keys
const QUERY_KEYS = {
  gitHubConnections: ['integrations', 'github', 'connections'],
  gitHubRepositories: (connectionId: number) => ['integrations', 'github', 'repositories', connectionId],
  gitLabConnections: ['integrations', 'gitlab', 'connections'],
  gitLabProjects: (connectionId: number) => ['integrations', 'gitlab', 'projects', connectionId],
  azureDevOpsConnections: ['integrations', 'azure-devops', 'connections'],
  azureDevOpsProjects: (connectionId: number) => ['integrations', 'azure-devops', 'projects', connectionId],
  jiraConnections: ['integrations', 'jira', 'connections'],
  jiraIssueLinks: (connectionId: number) => ['integrations', 'jira', 'issue-links', connectionId],
  serviceNowConnections: ['integrations', 'servicenow', 'connections'],
  snykConnections: ['integrations', 'snyk', 'connections'],
  snykProjects: (connectionId: number) => ['integrations', 'snyk', 'projects', connectionId],
  notificationChannels: ['integrations', 'notifications', 'channels'],
  notificationPreferences: ['integrations', 'notifications', 'preferences'],
};

// GitHub Hooks
export function useGitHubConnections() {
  return useQuery<GitHubConnection[], Error>({
    queryKey: QUERY_KEYS.gitHubConnections,
    queryFn: () => integrationService.getGitHubConnections(),
  });
}

export function useGitHubRepositories(connectionId: number) {
  return useQuery<GitHubRepository[], Error>({
    queryKey: QUERY_KEYS.gitHubRepositories(connectionId),
    queryFn: () => integrationService.getGitHubRepositories(connectionId),
    enabled: connectionId > 0,
  });
}

export function useCreateGitHubConnection() {
  const queryClient = useQueryClient();

  return useMutation<GitHubConnection, Error, CreateGitHubConnectionRequest>({
    mutationFn: (request) => integrationService.createGitHubConnection(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.gitHubConnections });
    },
  });
}

export function useTestGitHubConnection() {
  return useMutation<ConnectionTestResult, Error, number>({
    mutationFn: (connectionId) => integrationService.testGitHubConnection(connectionId),
  });
}

export function useDeleteGitHubConnection() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (connectionId) => integrationService.deleteGitHubConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.gitHubConnections });
    },
  });
}

export function useSyncGitHubRepositories() {
  const queryClient = useQueryClient();

  return useMutation<SyncResult, Error, number>({
    mutationFn: (connectionId) => integrationService.syncGitHubRepositories(connectionId),
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.gitHubRepositories(connectionId) });
    },
  });
}

// GitLab Hooks
export function useGitLabConnections() {
  return useQuery<GitLabConnection[], Error>({
    queryKey: QUERY_KEYS.gitLabConnections,
    queryFn: () => integrationService.getGitLabConnections(),
  });
}

export function useGitLabProjects(connectionId: number) {
  return useQuery<GitLabProject[], Error>({
    queryKey: QUERY_KEYS.gitLabProjects(connectionId),
    queryFn: () => integrationService.getGitLabProjects(connectionId),
    enabled: connectionId > 0,
  });
}

export function useCreateGitLabConnection() {
  const queryClient = useQueryClient();

  return useMutation<GitLabConnection, Error, CreateGitLabConnectionRequest>({
    mutationFn: (request) => integrationService.createGitLabConnection(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.gitLabConnections });
    },
  });
}

export function useTestGitLabConnection() {
  return useMutation<ConnectionTestResult, Error, number>({
    mutationFn: (connectionId) => integrationService.testGitLabConnection(connectionId),
  });
}

export function useDeleteGitLabConnection() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (connectionId) => integrationService.deleteGitLabConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.gitLabConnections });
    },
  });
}

export function useSyncGitLabProjects() {
  const queryClient = useQueryClient();

  return useMutation<SyncResult, Error, number>({
    mutationFn: (connectionId) => integrationService.syncGitLabProjects(connectionId),
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.gitLabProjects(connectionId) });
    },
  });
}

// Azure DevOps Hooks
export function useAzureDevOpsConnections() {
  return useQuery<AzureDevOpsConnection[], Error>({
    queryKey: QUERY_KEYS.azureDevOpsConnections,
    queryFn: () => integrationService.getAzureDevOpsConnections(),
  });
}

export function useAzureDevOpsProjects(connectionId: number) {
  return useQuery<AzureDevOpsProject[], Error>({
    queryKey: QUERY_KEYS.azureDevOpsProjects(connectionId),
    queryFn: () => integrationService.getAzureDevOpsProjects(connectionId),
    enabled: connectionId > 0,
  });
}

export function useCreateAzureDevOpsConnection() {
  const queryClient = useQueryClient();

  return useMutation<AzureDevOpsConnection, Error, CreateAzureDevOpsConnectionRequest>({
    mutationFn: (request) => integrationService.createAzureDevOpsConnection(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.azureDevOpsConnections });
    },
  });
}

export function useTestAzureDevOpsConnection() {
  return useMutation<ConnectionTestResult, Error, number>({
    mutationFn: (connectionId) => integrationService.testAzureDevOpsConnection(connectionId),
  });
}

export function useDeleteAzureDevOpsConnection() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (connectionId) => integrationService.deleteAzureDevOpsConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.azureDevOpsConnections });
    },
  });
}

export function useSyncAzureDevOpsProjects() {
  const queryClient = useQueryClient();

  return useMutation<SyncResult, Error, number>({
    mutationFn: (connectionId) => integrationService.syncAzureDevOpsProjects(connectionId),
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.azureDevOpsProjects(connectionId) });
    },
  });
}

// Jira Hooks
export function useJiraConnections() {
  return useQuery<JiraConnection[], Error>({
    queryKey: QUERY_KEYS.jiraConnections,
    queryFn: () => integrationService.getJiraConnections(),
  });
}

export function useJiraIssueLinks(connectionId: number) {
  return useQuery<JiraIssueLink[], Error>({
    queryKey: QUERY_KEYS.jiraIssueLinks(connectionId),
    queryFn: () => integrationService.getJiraIssueLinks(connectionId),
    enabled: connectionId > 0,
  });
}

export function useCreateJiraConnection() {
  const queryClient = useQueryClient();

  return useMutation<JiraConnection, Error, CreateJiraConnectionRequest>({
    mutationFn: (request) => integrationService.createJiraConnection(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.jiraConnections });
    },
  });
}

export function useTestJiraConnection() {
  return useMutation<ConnectionTestResult, Error, number>({
    mutationFn: (connectionId) => integrationService.testJiraConnection(connectionId),
  });
}

export function useDeleteJiraConnection() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (connectionId) => integrationService.deleteJiraConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.jiraConnections });
    },
  });
}

// ServiceNow Hooks
export function useServiceNowConnections() {
  return useQuery<ServiceNowConnection[], Error>({
    queryKey: QUERY_KEYS.serviceNowConnections,
    queryFn: () => integrationService.getServiceNowConnections(),
  });
}

export function useCreateServiceNowConnection() {
  const queryClient = useQueryClient();

  return useMutation<ServiceNowConnection, Error, CreateServiceNowConnectionRequest>({
    mutationFn: (request) => integrationService.createServiceNowConnection(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.serviceNowConnections });
    },
  });
}

export function useTestServiceNowConnection() {
  return useMutation<ConnectionTestResult, Error, number>({
    mutationFn: (connectionId) => integrationService.testServiceNowConnection(connectionId),
  });
}

export function useDeleteServiceNowConnection() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (connectionId) => integrationService.deleteServiceNowConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.serviceNowConnections });
    },
  });
}

// Snyk Hooks
export function useSnykConnections() {
  return useQuery<SnykConnection[], Error>({
    queryKey: QUERY_KEYS.snykConnections,
    queryFn: () => integrationService.getSnykConnections(),
  });
}

export function useSnykProjects(connectionId: number) {
  return useQuery<SnykProject[], Error>({
    queryKey: QUERY_KEYS.snykProjects(connectionId),
    queryFn: () => integrationService.getSnykProjects(connectionId),
    enabled: connectionId > 0,
  });
}

export function useCreateSnykConnection() {
  const queryClient = useQueryClient();

  return useMutation<SnykConnection, Error, CreateSnykConnectionRequest>({
    mutationFn: (request) => integrationService.createSnykConnection(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.snykConnections });
    },
  });
}

export function useTestSnykConnection() {
  return useMutation<ConnectionTestResult, Error, number>({
    mutationFn: (connectionId) => integrationService.testSnykConnection(connectionId),
  });
}

export function useDeleteSnykConnection() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (connectionId) => integrationService.deleteSnykConnection(connectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.snykConnections });
    },
  });
}

export function useSyncSnykProjects() {
  const queryClient = useQueryClient();

  return useMutation<SyncResult, Error, number>({
    mutationFn: (connectionId) => integrationService.syncSnykProjects(connectionId),
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.snykConnections });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.snykProjects(connectionId) });
    },
  });
}

export function useSyncSnykVulnerabilities() {
  const queryClient = useQueryClient();

  return useMutation<SyncResult, Error, number>({
    mutationFn: (connectionId) => integrationService.syncSnykVulnerabilities(connectionId),
    onSuccess: (_, connectionId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.snykProjects(connectionId) });
    },
  });
}

// Notification Hooks
export function useNotificationChannels() {
  return useQuery<NotificationChannel[], Error>({
    queryKey: QUERY_KEYS.notificationChannels,
    queryFn: () => integrationService.getNotificationChannels(),
  });
}

export function useNotificationPreferences() {
  return useQuery<NotificationPreference[], Error>({
    queryKey: QUERY_KEYS.notificationPreferences,
    queryFn: () => integrationService.getNotificationPreferences(),
  });
}

export function useCreateNotificationChannel() {
  const queryClient = useQueryClient();

  return useMutation<NotificationChannel, Error, CreateNotificationChannelRequest>({
    mutationFn: (request) => integrationService.createNotificationChannel(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationChannels });
    },
  });
}

export function useTestNotificationChannel() {
  return useMutation<ConnectionTestResult, Error, number>({
    mutationFn: (channelId) => integrationService.testNotificationChannel(channelId),
  });
}

export function useDeleteNotificationChannel() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, number>({
    mutationFn: (channelId) => integrationService.deleteNotificationChannel(channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationChannels });
    },
  });
}

export function useUpdateNotificationPreference() {
  const queryClient = useQueryClient();

  return useMutation<NotificationPreference, Error, Partial<NotificationPreference> & { id: number }>({
    mutationFn: (preference) => integrationService.updateNotificationPreference(preference),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.notificationPreferences });
    },
  });
}
